import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["columns", "children"];
import { View } from 'foundations-components/components/View';
import { PPUIGrid } from '../grid/PPUIGrid';
import { useMedia } from '../hooks/useMedia';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Single shadow wrapper that wraps all cards when they should be touching
 */
export const UnifiedShadowWrapper = () => {
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-default",
    boxShadow: "100",
    blockSize: "100%",
    inlineSize: "100%",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-subtle",
    borderRadius: "300",
    "aria-hidden": "true"
  });
};

/**
 * This is a basic div that will be rendered vertically at 100% under existing grid
 * columns and will display an shadow under it
 */
export const ShadowColumn = ({
  backgroundColor
}) => {
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: backgroundColor || 'fill-surface-default',
    boxShadow: "100",
    blockSize: "100%",
    inlineSize: "100%",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-subtle",
    borderRadius: "100",
    "aria-hidden": "true"
  });
};
const ShadowColumns = ({
  columns
}) => {
  const cols = [];
  for (let i = 0; i < columns; i++) {
    cols.push( /*#__PURE__*/_jsx(ShadowColumn, {
      "aria-hidden": "true"
    }, `shadow-column-${i}`));
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: cols
  });
};

/**
 * Builds the grid template areas for the shadow columns
 * @param columnCount - The number of columns in the grid
 * @returns The grid template areas, in this format:
 * "Title Title Title"
 * "Subheading Subheading Subheading"
 * "Pricing Pricing Pricing"
 * "Seats Seats Seats"
 * "Term Term Term"
 * "CTAs CTAs CTAs"
 * "Credits Credits Credits"
 * "Subtext Subtext Subtext"
 * "Features Features Features"
 * "Footer Footer Footer"
 */
const buildGridTemplateAreas = columnCount => {
  const sections = ['Title', 'Subheading', 'Pricing', 'Seats', 'Term', 'CTAs', 'Credits', 'Subtext', 'Features', 'Footer'];
  return sections.map(section => `"${Array(columnCount).fill(section).join(' ')}"`).join('\n');
};
export const MerchCardShadowColumns = ({
  columns,
  children,
  shadowColumnsComponent,
  isUngatedForInbound25 = false
}) => {
  const isMaxWidthSdMac = useMedia('sdMac');
  const defaultGridProps = {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    columnGap: isUngatedForInbound25 ? '0' : columns > 2 ? '400' : '1400',
    maxInlineSize: columns > 2 ? 'initial' : `calc(350px * ${columns})`,
    inlineSize: '100%',
    insetInlineStart: '0',
    insetInlineEnd: '0',
    marginInline: 'auto'
  };
  const sdMacGridProps = {
    columnGap: isUngatedForInbound25 ? '0' : columns > 2 ? '0' : '400'
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PPUIGrid, Object.assign({
      position: "relative"
    }, defaultGridProps, isMaxWidthSdMac ? sdMacGridProps : {}, {
      gridTemplateAreas: buildGridTemplateAreas(columns),
      children: children
    })), isUngatedForInbound25 ? /*#__PURE__*/_jsx(PPUIGrid, Object.assign({
      "aria-hidden": "true",
      blockSize: "100%",
      position: "absolute",
      insetBlockStart: "0",
      zIndex: "-1"
    }, Object.assign({}, defaultGridProps, {
      gridTemplateColumns: '1fr'
    }), isMaxWidthSdMac ? sdMacGridProps : {}, {
      children: /*#__PURE__*/_jsx(UnifiedShadowWrapper, {})
    })) : /*#__PURE__*/_jsx(PPUIGrid, Object.assign({
      "aria-hidden": "true",
      blockSize: "100%",
      position: "absolute",
      insetBlockStart: "0",
      zIndex: "-1"
    }, defaultGridProps, isMaxWidthSdMac ? sdMacGridProps : {}, {
      children: shadowColumnsComponent || /*#__PURE__*/_jsx(ShadowColumns, {
        columns: columns
      })
    }))]
  });
};
export const MerchCardColumns = _ref => {
  let {
      columns,
      children
    } = _ref,
    gridProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(PPUIGrid, Object.assign({
    position: "relative",
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    columnGap: "0",
    maxInlineSize: columns > 2 ? 'initial' : `calc(350px * ${columns})`,
    inlineSize: "100%",
    marginInline: "auto",
    backgroundColor: "fill-surface-default",
    boxShadow: "100",
    blockSize: "100%",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-subtle",
    borderRadius: "300",
    gridTemplateAreas: buildGridTemplateAreas(columns)
  }, gridProps, {
    children: children
  }));
};